import { TRIGGER_WIND_LIMITS } from './wind';
import {
  blockAsParsed,
  encountersDefaults,
  storeDefaults,
  cosmeticDefaults,
  missionDefaults,
  triggerCurrencyDefaults,
  triggerDefaults,
  triggerEncounterDefaults,
  triggerEventDefaults,
  triggerGeofenceDefaults,
  triggerHealthDefaults,
  triggerItemsDefaults,
  triggerRaidDefaults,
  triggerRulesDefaults,
  withDefaults,
  worldEventsDefaults,
} from '../teamsDefaults';
import {
  type TriggerRulesDef,
  type TriggerEncounterBlock,
  type TriggerDef,
  type MissionDef,
  type CosmeticDef,
  type Bundle,
  WORLD_EVENT_TYPES,
  TRIGGER_ACTIONS,
  TRIGGER_BLOCKS,
  ENCOUNTER_KINDS,
  TRIGGER_STATUSES,
  TRIGGER_SCOPES,
  TRIGGER_LIMITS,
  TRIGGER_ITEM_DESTINATIONS,
  TRIGGER_HEALTH_TARGETS,
  TRIGGER_CURRENCY_KINDS,
  MISSION_VISIBILITIES,
  MISSION_REWARD_KINDS,
  MISSION_REWARD_TAGS,
  MAX_MISSION_REWARD_TILES,
  COSMETIC_KINDS,
  TRIGGER_LIMITS_MAX,
  TRIGGER_RAID_ROOMS,
  TRIGGER_PAYLOAD_VERSION,
} from './types';
import { isBlank, num, type Err, type Ids } from './common';

export type TriggerRefSets = Record<'items' | 'tables' | 'zombies' | 'categories', Set<string>>;

export const oneOf = (list: readonly string[], v: string | null | undefined) =>
  v != null && list.includes(v);

/**
 * The scan trigger sections: a port of packages/shared-cs/Runtime/Content/ContentValidator.Triggers.cs (`TriggerSections`,
 * `Trigger`, `Blocks`, `Encounter`, `GrantItems`, `Rules`, `Missions`, `Cosmetics`) with the same messages in the same
 * order, plus `ContentValidator.Warnings` as client warnings. An absent section is empty (older bundles) and an explicit
 * `null` is missing; entries, rules and blocks are checked with their C# defaults filled in, like the server's parser.
 */
export function validateTriggers(b: Bundle, refs: TriggerRefSets, err: Err, warn: Err, ids: Ids) {
  function section<T>(
    key: 'triggers' | 'missions' | 'cosmetics',
    list: (T | null)[] | null | undefined,
  ) {
    const out: [T, number][] = [];
    if (list === null) {
      err(key, undefined, '', `Bundle is missing '${key}'.`);
      return out;
    }
    (list ?? []).forEach((x, i) => {
      if (x == null) err(key, i, '', `Bundle has an empty entry in '${key}'.`);
      else out.push([x, i]);
    });
    return out;
  }
  const triggers = section<TriggerDef>('triggers', b.triggers).map(
    ([t, i]) => [withDefaults(triggerDefaults(), t), i] as const,
  );
  const missions = section<MissionDef>('missions', b.missions).map(
    ([m, i]) => [withDefaults(missionDefaults(), m), i] as const,
  );
  const cosmetics = section<CosmeticDef>('cosmetics', b.cosmetics).map(
    ([c, i]) => [withDefaults(cosmeticDefaults(), c), i] as const,
  );

  const triggerIds = ids('triggers', b.triggers ?? [], 'trigger');
  const missionIds = ids('missions', b.missions ?? [], 'mission');
  const cosmeticIds = ids('cosmetics', b.cosmetics ?? [], 'cosmetic');

  const bosses = new Set<string>();
  for (const x of b.bosses ?? []) if (x?.id) bosses.add(x.id);
  const fights = new Set<string>();
  for (const e of blockAsParsed(encountersDefaults, b.encounters)?.types ?? [])
    if (e && num(e.roomCount) > 0 && e.id) fights.add(e.id);
  const events = new Set<string>();
  for (const e of blockAsParsed(worldEventsDefaults, b.worldEvents)?.types ?? [])
    if (e?.id) events.add(e.id);

  type Fail = (path: string, message: string) => void;

  function encounter(e: TriggerEncounterBlock, where: string, fail: Fail) {
    const kind = e.kind ?? '';
    if (!oneOf(ENCOUNTER_KINDS, kind)) {
      fail('encounter.kind', `${where} has unknown encounter kind '${kind}'.`);
      return;
    }
    if (kind !== 'boss' && e.bossId)
      fail('encounter.bossId', `${where} has a bossId, but its encounter kind is '${kind}'.`);
    if (kind !== 'ambush' && e.encounterId)
      fail(
        'encounter.encounterId',
        `${where} has an encounterId, but its encounter kind is '${kind}'.`,
      );
    if (kind !== 'raid' && e.raid != null)
      fail('encounter.raid', `${where} has a raid block, but its encounter kind is '${kind}'.`);
    if (kind === 'boss') {
      if (!bosses.has(e.bossId ?? ''))
        fail('encounter.bossId', `${where} fights unknown boss '${e.bossId ?? ''}'.`);
      return;
    }
    if (kind === 'ambush') {
      if (!fights.has(e.encounterId ?? ''))
        fail(
          'encounter.encounterId',
          `${where} uses unknown encounter fight '${e.encounterId ?? ''}' (an encounters type with roomCount above 0).`,
        );
      return;
    }
    if (e.raid == null) {
      fail('encounter.raid', `${where} needs a raid block for encounter kind 'raid'.`);
      return;
    }
    const r = withDefaults(triggerRaidDefaults(), e.raid);
    const base = 'encounter.raid';
    if (!refs.categories.has(r.category ?? ''))
      fail(`${base}.category`, `${where} raids unknown category '${r.category ?? ''}'.`);
    if (num(r.difficulty) < 1 || num(r.difficulty) > 5)
      fail(`${base}.difficulty`, `${where} needs raid difficulty 1..5.`);
    if (!refs.tables.has(r.lootTableId ?? ''))
      fail(`${base}.lootTableId`, `${where} uses unknown loot table '${r.lootTableId ?? ''}'.`);
    if (num(r.lootRolls) < 0) fail(`${base}.lootRolls`, `${where} raid.lootRolls is negative.`);
    const R = TRIGGER_RAID_ROOMS;
    if (num(r.rooms) < R.min || num(r.rooms) > R.max)
      fail(`${base}.rooms`, `${where} needs raid rooms ${R.min}..${R.max}.`);
    if (r.zombieIds === null)
      fail(`${base}.zombieIds`, `${where} has a null 'raid.zombieIds' list.`);
    (r.zombieIds ?? []).forEach((z, j) => {
      if (!refs.zombies.has(z ?? ''))
        fail(`${base}.zombieIds.${j}`, `${where} uses unknown zombie '${z ?? ''}'.`);
    });
  }

  /** Exactly one block, the one matching the action, then that block's fields (`ContentValidator.Blocks`). */
  function blocks(t: TriggerDef, where: string, fail: Fail) {
    for (const action of TRIGGER_ACTIONS) {
      const key = TRIGGER_BLOCKS[action];
      const set = t[key] != null;
      if (action === t.action && !set)
        fail(key, `${where} needs block '${key}' for action '${t.action}'.`);
      if (action !== t.action && set)
        fail(
          key,
          `${where} has block '${key}', but its action is '${t.action ?? ''}' (exactly one block, matching the action).`,
        );
    }
    const L = TRIGGER_LIMITS_MAX;
    if (t.encounter != null && t.action === 'start_encounter')
      encounter(withDefaults(triggerEncounterDefaults(), t.encounter), where, fail);
    if (t.items != null && t.action === 'grant_items') {
      const g = withDefaults(triggerItemsDefaults(), t.items);
      if (g.stacks === null) fail('items.stacks', `${where} has a null 'items.stacks' list.`);
      const stacks = g.stacks ?? [];
      if (g.stacks != null && stacks.length === 0)
        fail('items.stacks', `${where} grants no items.`);
      stacks.forEach((st, j) => {
        const base = `items.stacks.${j}`;
        if (st == null) {
          fail(base, `${where} has an empty item stack.`);
          return;
        }
        const id = st.itemId ?? '';
        if (!refs.items.has(id)) fail(`${base}.itemId`, `${where} grants unknown item '${id}'.`);
        const q = num(st.quantity);
        if (q < 1 || q > L.quantity)
          fail(`${base}.quantity`, `${where} needs item quantity 1..${L.quantity} ('${id}').`);
      });
      if (!oneOf(TRIGGER_ITEM_DESTINATIONS, g.destination))
        fail(
          'items.destination',
          `${where} has unknown item destination '${g.destination ?? ''}'.`,
        );
    }
    if (t.health != null && t.action === 'grant_health') {
      const h = withDefaults(triggerHealthDefaults(), t.health);
      if (num(h.amount) < 1 || num(h.amount) > L.health)
        fail('health.amount', `${where} needs health amount 1..${L.health}.`);
      if (!oneOf(TRIGGER_HEALTH_TARGETS, h.target))
        fail('health.target', `${where} has unknown health target '${h.target ?? ''}'.`);
    }
    if (t.wind != null && t.action === 'grant_wind') {
      // ContentValidator.Triggers.cs (ADR 0073): the wind block's target defaults to the player like the health block's.
      const winds = num(t.wind.winds);
      const target = t.wind.target ?? 'player';
      if (!(winds >= TRIGGER_WIND_LIMITS.min && winds <= TRIGGER_WIND_LIMITS.max))
        fail(
          'wind.winds',
          `${where} needs wind winds ${TRIGGER_WIND_LIMITS.min}..${TRIGGER_WIND_LIMITS.max}.`,
        );
      if (!oneOf(TRIGGER_HEALTH_TARGETS, target))
        fail('wind.target', `${where} has unknown wind target '${target}'.`);
    }
    if (t.currency != null && t.action === 'grant_currency') {
      const c = withDefaults(triggerCurrencyDefaults(), t.currency);
      if (!oneOf(TRIGGER_CURRENCY_KINDS, c.kind))
        fail('currency.kind', `${where} has unknown currency kind '${c.kind ?? ''}'.`);
      if (num(c.amount) < 1 || num(c.amount) > L.energy)
        fail('currency.amount', `${where} needs currency amount 1..${L.energy}.`);
    }
    if (t.cosmetic != null && t.action === 'grant_cosmetic') {
      const id = t.cosmetic.cosmeticId ?? '';
      if (!cosmeticIds.has(id))
        fail('cosmetic.cosmeticId', `${where} grants unknown cosmetic '${id}'.`);
    }
    if (t.mission != null && t.action === 'start_mission') {
      const id = t.mission.missionId ?? '';
      if (!missionIds.has(id))
        fail('mission.missionId', `${where} starts unknown mission '${id}'.`);
    }
    if (t.event != null && t.action === 'start_event') {
      const e = withDefaults(triggerEventDefaults(), t.event);
      const type = e.type ?? '';
      if (!oneOf(WORLD_EVENT_TYPES, type) || !events.has(type))
        fail('event.type', `${where} starts unknown event type '${type}'.`);
      const d = num(e.maxDistanceM);
      if (!(d > 0 && d <= L.eventDistanceM))
        fail(
          'event.maxDistanceM',
          `${where} needs event maxDistanceM above 0 and at most ${L.eventDistanceM}.`,
        );
    }
  }

  function rules(r: TriggerRulesDef, where: string, fail: Fail) {
    if (!oneOf(TRIGGER_SCOPES, r.scope))
      fail('rules.scope', `${where} has unknown scope '${r.scope ?? ''}'.`);
    if (!oneOf(TRIGGER_LIMITS, r.limit))
      fail('rules.limit', `${where} has unknown limit '${r.limit ?? ''}'.`);
    const cd = num(r.cooldownHours);
    if (!(cd >= 0)) fail('rules.cooldownHours', `${where} has a negative cooldownHours.`);
    if (r.limit !== 'repeatable' && cd > 0)
      fail(
        'rules.cooldownHours',
        `${where} has cooldownHours, but only a 'repeatable' limit uses them.`,
      );
    if (r.maxPerPlayer != null && r.maxPerPlayer < 1)
      fail('rules.maxPerPlayer', `${where} needs maxPerPlayer of at least 1 (or null).`);
    if (r.maxPerPlayer != null && r.limit !== 'repeatable')
      fail(
        'rules.maxPerPlayer',
        `${where} has maxPerPlayer, but only a 'repeatable' limit uses it.`,
      );
    if (r.maxTotal != null && r.maxTotal < 1)
      fail('rules.maxTotal', `${where} needs maxTotal of at least 1 (or null).`);
    if (r.activeFrom != null && r.activeUntil != null && r.activeFrom >= r.activeUntil)
      fail('rules.activeUntil', `${where} needs activeFrom before activeUntil.`);
    if (r.season != null && r.season.trim().length === 0)
      fail('rules.season', `${where} has a blank season (use null for any).`);
    if (r.geofence != null) {
      const g = withDefaults(triggerGeofenceDefaults(), r.geofence);
      const lat = num(g.lat);
      const lon = num(g.lon);
      if (!(lat >= -90 && lat <= 90) || !(lon >= -180 && lon <= 180))
        fail('rules.geofence', `${where} has a geofence outside lat -90..90, lon -180..180.`);
      if (!(num(g.radiusM) > 0))
        fail('rules.geofence.radiusM', `${where} needs a geofence radiusM above 0.`);
    }
  }

  for (const [t, i] of triggers) {
    const where = `Trigger '${t.id ?? ''}'`;
    const fail: Fail = (path, message) => err('triggers', i, path, message);
    if (isBlank(t.title)) fail('title', `${where} needs a title.`);
    if (!/^[A-Z]{2,12}$/.test(t.codePrefix ?? ''))
      fail('codePrefix', `${where} needs a codePrefix of 2-12 capital letters.`);
    if (!oneOf(TRIGGER_STATUSES, t.status))
      fail('status', `${where} has unknown status '${t.status ?? ''}'.`);
    if (t.missionId && !missionIds.has(t.missionId))
      fail('missionId', `${where} belongs to unknown mission '${t.missionId}'.`);

    if (!oneOf(TRIGGER_ACTIONS, t.action)) {
      fail('action', `${where} has unknown action '${t.action ?? ''}'.`);
    } else {
      const v = num(t.payloadVersion);
      if (v < 1 || v > TRIGGER_PAYLOAD_VERSION)
        fail('payloadVersion', `${where} needs payloadVersion 1..${TRIGGER_PAYLOAD_VERSION}.`);
      blocks(t, where, fail);
    }

    if (t.rules === null) fail('rules', `${where} is missing 'rules'.`);
    else rules(withDefaults(triggerRulesDefaults(), t.rules ?? {}), where, fail);
  }

  // Missions (`ContentValidator.Missions`).
  const orders = new Set<number>();
  for (const [m, i] of missions) {
    const where = `Mission '${m.id ?? ''}'`;
    const fail: Fail = (path, message) => err('missions', i, path, message);
    if (isBlank(m.title)) fail('title', `${where} needs a title.`);
    // A staged mission (scenario layer) needn't start from a QR: its trigger is optional.
    const staged = (m.stages ?? []).length > 0;
    if (!(staged && isBlank(m.triggerId)) && !triggerIds.has(m.triggerId ?? ''))
      fail('triggerId', `${where} uses unknown trigger '${m.triggerId ?? ''}'.`);
    const order = num(m.order);
    if (orders.has(order))
      fail('order', `${where} repeats order ${order} (each mission needs its own).`);
    else orders.add(order);
    if (!oneOf(MISSION_VISIBILITIES, m.visibility))
      fail('visibility', `${where} has unknown visibility '${m.visibility ?? ''}'.`);
    if (!oneOf(TRIGGER_STATUSES, m.status))
      fail('status', `${where} has unknown status '${m.status ?? ''}'.`);
    if (!oneOf(TRIGGER_SCOPES, m.scope))
      fail('scope', `${where} has unknown scope '${m.scope ?? ''}'.`);
  }

  // Cosmetics and the pass's cosmeticIds (`ContentValidator.Cosmetics`). No catalogue at all skips the pass check.
  for (const [c, i] of cosmetics) {
    const where = `Cosmetic '${c.id ?? ''}'`;
    if (isBlank(c.name)) err('cosmetics', i, 'name', `${where} needs a name.`);
    if (!oneOf(COSMETIC_KINDS, c.kind))
      err(
        'cosmetics',
        i,
        'kind',
        `${where} has unknown kind '${c.kind ?? ''}' (outfit, marker or diorama).`,
      );
  }
  if (cosmetics.length > 0) {
    const store = blockAsParsed(storeDefaults, b.store);
    (store?.products ?? []).forEach((p, j) => {
      (p?.cosmeticIds ?? []).forEach((id, k) => {
        if (!cosmeticIds.has(id ?? ''))
          err(
            'store',
            undefined,
            `products.${j}.cosmeticIds.${k}`,
            `Store product '${p?.id ?? ''}' uses unknown cosmetic '${id ?? ''}'.`,
          );
      });
    });
  }

  // `ContentValidator.Warnings`: publishing still works, but the publisher should see these.
  for (const [t, i] of triggers)
    if (t.rules?.limit === 'unlimited' && t.status !== 'retired')
      warn(
        'triggers',
        i,
        'rules.limit',
        `Trigger '${t.id ?? ''}' has limit 'unlimited' (for dev and test only).`,
      );
}

/**
 * `missions[].rewards` (ADR 0062): a port of shared-cs `ContentValidator.MissionRewards.cs` with the same messages, in the server's
 * position (after the story sections; `validateStoryIssues` calls it). The tiles are display data: known kind and tag, an existing item or
 * cosmetic where the kind names one, a name where it doesn't, quantity 0..999 (0 = unknown), at most six, none on a story issue.
 */
export function validateMissionRewards(b: Bundle, err: Err) {
  const items = new Set<string>();
  for (const i of b.items ?? []) if (i?.id) items.add(i.id);
  const cosmetics = new Set<string>();
  for (const c of b.cosmetics ?? []) if (c?.id) cosmetics.add(c.id);
  (b.missions ?? []).forEach((m, i) => {
    const rewards = m?.rewards;
    if (m == null || rewards == null || rewards.length === 0) return;
    const where = `Mission '${m.id ?? ''}'`;
    const fail = (path: string, message: string) => err('missions', i, path, message);
    if (m.story != null)
      fail(
        'rewards',
        `${where} is a story issue: its reward tiles come from story.rewards, so leave rewards empty.`,
      );
    if (rewards.length > MAX_MISSION_REWARD_TILES)
      fail(
        'rewards',
        `${where} has ${rewards.length} reward tiles (at most ${MAX_MISSION_REWARD_TILES}).`,
      );
    rewards.forEach((raw, n) => {
      if (raw == null) {
        fail(`rewards.${n}`, `${where} has an empty reward tile.`);
        return;
      }
      const r = {
        kind: raw.kind ?? 'item',
        refId: raw.refId ?? '',
        name: raw.name ?? '',
        quantity: raw.quantity ?? 1,
        tag: raw.tag ?? 'first_clear',
      };
      const p = (k: string) => `rewards.${n}.${k}`;
      if (!oneOf(MISSION_REWARD_KINDS, r.kind))
        fail(p('kind'), `${where} has a reward of unknown kind '${r.kind}'.`);
      if (!oneOf(MISSION_REWARD_TAGS, r.tag))
        fail(p('tag'), `${where} has a reward with unknown tag '${r.tag}'.`);
      const namesItem = r.kind === 'item' || r.kind === 'haul';
      if (namesItem && r.refId !== '' && !items.has(r.refId))
        fail(p('refId'), `${where} rewards unknown item '${r.refId}'.`);
      if (r.kind === 'item' && r.refId === '')
        fail(p('refId'), `${where} has an item reward without an item.`);
      if (r.kind === 'cosmetic' && !cosmetics.has(r.refId))
        fail(p('refId'), `${where} rewards unknown cosmetic '${r.refId}'.`);
      if (r.name.trim() === '' && r.refId === '')
        fail(p('name'), `${where} has a ${r.kind} reward without a name.`);
      if (r.quantity < 0 || r.quantity > MAX_REWARD_QUANTITY)
        fail(
          p('quantity'),
          `${where} needs reward quantity 0..${MAX_REWARD_QUANTITY} (0 = unknown).`,
        );
    });
  });
}

/** shared-cs `ContentValidator.MaxGrantQuantity`. */
const MAX_REWARD_QUANTITY = 999;
